import React, { useState, useEffect } from "react"
import { CALENDAR_MONTHS } from "../../utils/exports/months"
import { db } from "../../utils/firebase"
import firebase from "firebase"
import moment from "moment"
import "./conversions-by-channel.scss"

// UI imports
import Tile from "../../components/structure/tile/tile"
import Title from "../../components/structure/title/title"
import Select from "../../components/ui/select/select"
import Table from "../../components/structure/table/table"
import Button from "../../components/ui/button/button"

// Build up a list of years for the select field
let yearsObj = {}
let nextYear = Number(moment().startOf("year").add(2, "years").format("YYYY"))
for (let i = nextYear; i >= 2018; i--) {
	yearsObj = {
		[i]: i,
		...yearsObj,
	}
}

// Define channel names for display
const channelNames = {
	google: "Google Ads",
	bing: "Bing Ads",
	facebook: "Facebook Ads",
	newsletter: "Newsletter",
	socials: "Social Media",
	organic: "Organic",
	system: "System (Phone/Freephone)",
}

// Returns the HTML markup for the conversions by channel page
export default function ConversionsByChannel() {
	const [loading, setLoading] = useState(false)

	const [month, setMonth] = useState("")
	const [year, setYear] = useState("")

	const [channelRows, setChannelRows] = useState([])

	// Determine the channel for an enquiry based on tracking data
	// This matches the logic from margin-attribution.js
	const getChannelFromEnquiry = (enquiry) => {
		// Map a few of the sources into different names
		let baseChannel = "organic"

		// First check UTM source
		if (enquiry.tracking?.utm_source && enquiry.tracking?.utm_source !== "Golf Holidays Direct") {
			baseChannel = enquiry.tracking?.utm_source === "bing_ads" ? "bing" : enquiry.tracking?.utm_source
		} else if (enquiry.tracking?.utm_source === "Golf Holidays Direct") {
			baseChannel = "newsletter"
		} else if (enquiry.site === "SYSTEM") {
			baseChannel = "system"
		}

		// Then check inbound channel which can override UTM source in some cases
		if (enquiry.inbound_channel) {
			if (enquiry.inbound_channel === "EMAIL" && enquiry.tracking?.utm_source) {
				// Sales@ Email with tracking - use the UTM source
				baseChannel = enquiry.tracking?.utm_source === "bing_ads" ? "bing" : enquiry.tracking?.utm_source
			} else if (enquiry.inbound_channel === "EMAIL_MARKETING") {
				baseChannel = "newsletter"
			} else if (enquiry.inbound_channel === "FACEBOOK") {
				baseChannel = "socials"
			} else if (enquiry.inbound_channel === "INSTAGRAM") {
				baseChannel = "socials"
			}
		}

		return baseChannel
	}

	// Fetch the conversion data from the database
	const fetchConversionsData = async () => {
		// Reset the state
		setLoading(true)

		// Get the milliseconds timestamps from the chosen filters
		let fetchStart = 0
		let fetchEnd = 0
		if (month && year) {
			fetchStart = moment(`${month}-${year}`, "MMMM-YYYY").startOf("month").valueOf()
			fetchEnd = moment(`${month}-${year}`, "MMMM-YYYY").endOf("month").valueOf()
		} else if (!month && year) {
			fetchStart = moment(`${year}`, "YYYY").startOf("year").valueOf()
			fetchEnd = moment(`${year}`, "YYYY").endOf("year").valueOf()
		}
		const startTimestamp = firebase.firestore.Timestamp.fromMillis(fetchStart)
		const endTimestamp = firebase.firestore.Timestamp.fromMillis(fetchEnd)

		try {
			// Fetch bookings first to get all client IDs
			const bookingsSnapshot = await db.collection("bookings").where("enquiry.created", ">=", startTimestamp).where("enquiry.created", "<=", endTimestamp).get()

			// Collect all unique client IDs from bookings
			const clientIds = new Set()
			const bookingsData = []

			bookingsSnapshot.forEach((doc) => {
				const bookingData = doc.data()
				if (bookingData.client) {
					clientIds.add(bookingData.client)
				}
				bookingsData.push({
					bookingID: doc.id,
					...bookingData,
				})
			})

			// Fetch all removed enquiries for clients with bookings (to get tracking data)
			// Go back 3 months from the start date to be safe
			const threeMonthsBack = moment(fetchStart).subtract(3, "months").valueOf()
			const threeMonthsBackTimestamp = firebase.firestore.Timestamp.fromMillis(threeMonthsBack)

			const removedEnquiriesMap = new Map()
			const enquiryPromises = Array.from(clientIds).map(async (clientId) => {
				const enquiryDocs = await db.collection("enquiries_removed").where("enquiry.client", "==", clientId).where("enquiry.created", ">=", threeMonthsBackTimestamp).get()

				const clientEnquiries = []
				enquiryDocs.forEach((enquiry) => {
					clientEnquiries.push({
						...enquiry.data()?.enquiry,
						enquiryID: enquiry.id,
					})
				})
				removedEnquiriesMap.set(clientId, clientEnquiries)
			})
			await Promise.all(enquiryPromises)

			// Fetch active enquiries (for enquiry counts)
			const activeEnquiries = await db.collection("enquiries").where("created", ">=", startTimestamp).where("created", "<=", endTimestamp).get()

			// Fetch removed enquiries in date range (for enquiry counts)
			const removedEnquiries = await db.collection("enquiries_removed").where("enquiry.created", ">=", startTimestamp).where("enquiry.created", "<=", endTimestamp).get()

			// Process enquiries and group by channel
			const channelEnquiries = {}
			const channelBookings = {}

			// Process active enquiries (exclude archived from stats)
			activeEnquiries.forEach((doc) => {
				const enquiry = doc.data()
				// Skip archived enquiries
				if (enquiry.archived_from_stats === true) return
				
				const channel = getChannelFromEnquiry(enquiry)

				if (!channelEnquiries[channel]) {
					channelEnquiries[channel] = new Map()
				}
				channelEnquiries[channel].set(enquiry.client, enquiry)
			})

			// Process removed enquiries (exclude archived from stats)
			removedEnquiries.forEach((doc) => {
				const data = doc.data()
				// Skip archived enquiries
				if (data.archived_from_stats === true) return
				
				const enquiry = data.enquiry || data
				const channel = getChannelFromEnquiry(enquiry)

				if (!channelEnquiries[channel]) {
					channelEnquiries[channel] = new Map()
				}
				if (enquiry.client) {
					channelEnquiries[channel].set(enquiry.client, enquiry)
				}
			})

			// Process bookings - match with removed enquiries to get tracking data
			bookingsData.forEach((bookingData) => {
				const clientEnquiries = removedEnquiriesMap.get(bookingData.client) || []
				let enquiryData = null
				let trackingData = null

				// Find the matching enquiry by created timestamp
				if (bookingData.enquiry?.created) {
					const matchingEnquiry = clientEnquiries.find((enq) => enq.created?.seconds === bookingData.enquiry?.created?.seconds)

					if (matchingEnquiry) {
						enquiryData = matchingEnquiry
						if (matchingEnquiry.tracking) {
							trackingData = matchingEnquiry.tracking
						}
					}
				}

				// If there are multiple enquiries and this is a SYSTEM enquiry, try to find one with tracking
				if (clientEnquiries.length > 1 && enquiryData?.site === "SYSTEM") {
					const altEnquiryWithTracking = clientEnquiries.find((enquiry) => enquiry.tracking)
					if (altEnquiryWithTracking) {
						trackingData = altEnquiryWithTracking.tracking
						enquiryData = altEnquiryWithTracking
					}
				}

				// If no tracking data found in removed enquiries, use enquiry from booking
				if (!trackingData && bookingData.enquiry) {
					enquiryData = bookingData.enquiry
					trackingData = bookingData.enquiry.tracking
				}

				// Construct an enquiry-like object with tracking data
				const enquiryForChannel = {
					tracking: trackingData,
					site: enquiryData?.site || bookingData.enquiry?.site,
					inbound_channel: enquiryData?.inbound_channel || bookingData.enquiry?.inbound_channel,
				}

				const channel = getChannelFromEnquiry(enquiryForChannel)

				if (!channelBookings[channel]) {
					channelBookings[channel] = new Set()
				}
				channelBookings[channel].add(bookingData.bookingID)
			})

			// Calculate conversion rates for each channel
			// Combine all unique channels from both enquiries and bookings
			const allChannels = new Set([...Object.keys(channelEnquiries), ...Object.keys(channelBookings)])

			const results = Array.from(allChannels).map((channel) => {
				const enquiriesCount = channelEnquiries[channel] ? channelEnquiries[channel].size : 0
				const bookingsCount = channelBookings[channel] ? channelBookings[channel].size : 0
				const conversionRate = enquiriesCount > 0 ? ((bookingsCount / enquiriesCount) * 100).toFixed(2) : 0

				return {
					channel,
					name: channelNames[channel] || channel,
					enquiries: enquiriesCount,
					bookings: bookingsCount,
					conversionRate,
				}
			})

			// Sort by conversion rate (highest to lowest)
			results.sort((a, b) => parseFloat(b.conversionRate) - parseFloat(a.conversionRate))

			// Update state with results
			setChannelRows(results)
		} catch (err) {
			console.error("Error fetching conversion data:", err)
		} finally {
			setLoading(false)
		}
	}

	return (
		<Tile fullPage={true}>
			<Title className="flex has-select-field">
				<h1>Conversions by Channel</h1>
			</Title>

			<div className="page-filters extra-stats">
				<Select
					label="Month:"
					placeholder="Month:"
					value={month}
					activeOnHover={true}
					onSelect={(option) => setMonth(option.option)}
					options={CALENDAR_MONTHS}
				/>

				<Select
					label="Year:"
					placeholder="Year:"
					value={year}
					activeOnHover={true}
					onSelect={(option) => setYear(option.option)}
					options={yearsObj}
				/>

				<div style={{ display: "flex", gap: "10px", alignItems: "center" }}>
					<Button
						filters={true}
						disabled={(!month || !year) && !year}
						label="Fetch"
						loadingText="Fetching..."
						loading={loading}
						onClick={() => fetchConversionsData()}
					/>
				</div>
			</div>

			<Table
				className="conversions-by-channel-table"
				headings={["Channel", "Enquiries (De-Duped)", "Bookings", "Conversion Rate"]}
				noResults={!channelRows.length}
				noResultsMessage="Choose some parameters to fetch conversion data">
				{channelRows.map((channelRow) => (
					<tr key={channelRow.channel}>
						<td>{channelRow.name}</td>
						<td>{channelRow.enquiries}</td>
						<td>{channelRow.bookings}</td>
						<td>{channelRow.conversionRate}%</td>
					</tr>
				))}
				{channelRows.length > 0 &&
					(() => {
						// Calculate totals for each group
						const totalAllEnquiries = channelRows.reduce((total, row) => total + row.enquiries, 0)
						const totalAllBookings = channelRows.reduce((total, row) => total + row.bookings, 0)

						const totals = [
							{
								label: "Total (system)",
								channels: ["system"],
								enquiries: channelRows.filter((row) => ["system"].includes(row.channel)).reduce((total, row) => total + row.enquiries, 0),
								bookings: channelRows.filter((row) => ["system"].includes(row.channel)).reduce((total, row) => total + row.bookings, 0),
							},
							{
								label: "Total (paid ads)",
								channels: ["google", "bing", "facebook"],
								enquiries: channelRows.filter((row) => ["google", "bing", "facebook"].includes(row.channel)).reduce((total, row) => total + row.enquiries, 0),
								bookings: channelRows.filter((row) => ["google", "bing", "facebook"].includes(row.channel)).reduce((total, row) => total + row.bookings, 0),
							},
							{
								label: "Total (newsletter)",
								channels: ["newsletter"],
								enquiries: channelRows.filter((row) => ["newsletter"].includes(row.channel)).reduce((total, row) => total + row.enquiries, 0),
								bookings: channelRows.filter((row) => ["newsletter"].includes(row.channel)).reduce((total, row) => total + row.bookings, 0),
							},
							{
								label: "Total (socials)",
								channels: ["socials"],
								enquiries: channelRows.filter((row) => ["socials"].includes(row.channel)).reduce((total, row) => total + row.enquiries, 0),
								bookings: channelRows.filter((row) => ["socials"].includes(row.channel)).reduce((total, row) => total + row.bookings, 0),
							},
							{
								label: "Total (organic)",
								channels: ["organic"],
								enquiries: channelRows.filter((row) => ["organic"].includes(row.channel)).reduce((total, row) => total + row.enquiries, 0),
								bookings: channelRows.filter((row) => ["organic"].includes(row.channel)).reduce((total, row) => total + row.bookings, 0),
							},
						]
							.map((item) => ({
								...item,
								conversionRate: item.enquiries > 0 ? ((item.bookings / item.enquiries) * 100).toFixed(2) : "0.00",
								percentage: totalAllEnquiries > 0 ? ((item.enquiries / totalAllEnquiries) * 100).toFixed(1) : "0.0",
							}))
							.sort((a, b) => parseFloat(b.conversionRate) - parseFloat(a.conversionRate))

						return totals.map(({ label, enquiries, bookings, conversionRate, percentage }) => (
							<tr
								key={label}
								className="totals-row">
								<td>
									<strong>{label}</strong>
								</td>
								<td>
									<strong>
										{enquiries} ({percentage}%)
									</strong>
								</td>
								<td>
									<strong>{bookings}</strong>
								</td>
								<td>
									<strong>{conversionRate}%</strong>
								</td>
							</tr>
						))
					})()}
			</Table>
		</Tile>
	)
}
